import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { toast } from "sonner";
import { useDragHighlight } from "@/domains/documents/hooks/useDragHighlight";
import { hasExternalFiles, isInsideModal } from "@/domains/documents/utils/dragEvents";
import {
  carriesAppMovePayload,
  readMoveDragPayload,
  type MoveDragSelection,
} from "@/domains/documents/utils/documentMoveDrag";
import { extractDroppedFiles } from "@/domains/documents/utils/uploadSelection";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { logger } from "@/utils/logger";

/** How long a drag has to rest on the collapsed bar before the panel opens. */
export const SPRING_LOAD_DELAY_MS = 400;

/**
 * What a drag over the chat would attach: files from the OS, uploaded first, or
 * documents dragged out of the documents list, attached as they are.
 */
export type ChatDragKind = "files" | "documents";

function dragKindOf(dataTransfer: DataTransfer | null | undefined): ChatDragKind | null {
  if (hasExternalFiles(dataTransfer)) return "files";
  if (carriesAppMovePayload(dataTransfer)) return "documents";
  return null;
}

/** Tags whose elements count as "an editable element is focused" for the Ctrl+C toggle. */
const CHAT_TOGGLE_INPUT_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** Ctrl/Cmd+C with no other modifier. */
function isCopyChord(e: KeyboardEvent): boolean {
  if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return false;
  return e.key === "c" || e.key === "C";
}

/** Any selection at all — including non-text ranges, whose toString() is empty. */
function somethingIsSelected(): boolean {
  const selection = window.getSelection();
  return Boolean(selection && (!selection.isCollapsed || selection.toString().length > 0));
}

/**
 * Focus sits somewhere the chord means "copy": an editable element, or an
 * iframe / open shadow host whose selection we can't see from here.
 */
function focusOwnsTheChord(): boolean {
  const active = document.activeElement as HTMLElement | null;
  if (!active) return false;
  return (
    CHAT_TOGGLE_INPUT_TAGS.has(active.tagName) ||
    active.isContentEditable ||
    active.tagName === "IFRAME" ||
    Boolean(active.shadowRoot)
  );
}

export interface UseChatWindowInteractionsParams {
  isCollapsed: boolean;
  onExpand: () => void;
  onToggleCollapse: () => void;
  /** OS files dropped on the chat, folders already flattened into their files. */
  onFilesDropped: (files: File[]) => void;
  /** Documents (and possibly folders) dragged out of the documents list. */
  onDocumentsDropped: (payload: MoveDragSelection) => void;
}

type DropHandlers = {
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  onDragLeave: (e: DragEvent<HTMLElement>) => void;
  onDrop: (e: DragEvent<HTMLElement>) => void;
};

/**
 * The chat window's two direct-manipulation affordances: the Ctrl/Cmd+C
 * open-close hotkey and drag-and-drop attach (WHA-3351).
 *
 * Copy-safety of the hotkey is the fiddly part: native copy is never blocked —
 * the handler doesn't preventDefault, and it bails out entirely when any
 * selection exists (including non-text ranges like a selected image, whose
 * toString() is empty), when an editable element has focus, or when focus sits
 * on an iframe or an open shadow host — contexts whose selection
 * `window.getSelection()` can't see from the top document.
 *
 * Drops take two kinds of drag. OS files are uploaded and attached, with a
 * dropped folder flattened into the files inside it. Documents dragged from
 * the documents list carry the app's move payload (`writeMoveDragPayload`)
 * and are attached as they are. That drag advertises `effectAllowed = "move"`,
 * and a dragover answering with any other `dropEffect` turns the operation
 * into "none" — the browser then never fires `drop` — so the chat answers
 * "move" to it even though nothing moves.
 *
 * The collapsed bar is a drop target too: a drop there attaches and opens the
 * panel, and a drag resting on it opens the panel so the drop can land inside.
 */
export function useChatWindowInteractions({
  isCollapsed,
  onExpand,
  onToggleCollapse,
  onFilesDropped,
  onDocumentsDropped,
}: UseChatWindowInteractionsParams) {
  const { t } = useLanguage();
  const highlight = useDragHighlight();
  const { enter, leave, clear } = highlight;
  const [dragKind, setDragKind] = useState<ChatDragKind | null>(null);
  const springLoadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Opening from the collapsed bar goes through `onExpand` so the panel expands
  // upward and is clamped into the viewport (plain setCollapsed(false) would
  // grow the panel downward from the bar's y, mostly off-screen).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isCopyChord(e) || somethingIsSelected() || focusOwnsTheChord()) return;
      if (isCollapsed) onExpand();
      else onToggleCollapse();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isCollapsed, onExpand, onToggleCollapse]);

  const cancelSpringLoad = useCallback(() => {
    if (springLoadTimer.current === null) return;
    clearTimeout(springLoadTimer.current);
    springLoadTimer.current = null;
  }, []);

  useEffect(() => {
    const cancel = () => cancelSpringLoad();
    const cancelOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelSpringLoad();
    };
    window.addEventListener("drop", cancel, true);
    window.addEventListener("dragend", cancel, true);
    window.addEventListener("keydown", cancelOnEscape, true);
    return () => {
      window.removeEventListener("drop", cancel, true);
      window.removeEventListener("dragend", cancel, true);
      window.removeEventListener("keydown", cancelOnEscape, true);
    };
  }, [cancelSpringLoad]);

  // The bar unmounts as the panel opens, so its dragleave may never come.
  useEffect(() => {
    if (!isCollapsed) cancelSpringLoad();
  }, [isCollapsed, cancelSpringLoad]);
  useEffect(() => cancelSpringLoad, [cancelSpringLoad]);

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      const kind = dragKindOf(e.dataTransfer);
      if (!kind) return;
      // Claimed even inside the chat's own dialogs (the document picker, the
      // files panel), which take no drops: an unclaimed file drop there would
      // have the browser open the file in place of the app.
      e.preventDefault();
      e.stopPropagation();
      if (isInsideModal(e.target)) {
        e.dataTransfer.dropEffect = "none";
        clear();
        return;
      }
      e.dataTransfer.dropEffect = kind === "files" ? "copy" : "move";
      setDragKind(kind);
      enter();
    },
    [clear, enter]
  );

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLElement>) => {
      if (!dragKindOf(e.dataTransfer)) return;
      leave();
    },
    [leave]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLElement>): boolean | Promise<boolean> => {
      const kind = dragKindOf(e.dataTransfer);
      if (!kind) return false;
      e.preventDefault();
      e.stopPropagation();
      clear();
      if (isInsideModal(e.target)) return false;

      if (kind === "documents") {
        // Read now: the payload and its provenance flag only last as long as
        // the drop's own dispatch.
        const payload = readMoveDragPayload(e.dataTransfer);
        if (payload) onDocumentsDropped(payload);
        return Boolean(payload?.docIds.length);
      }
      return extractDroppedFiles(e.dataTransfer)
        .then((selection) => {
          if (selection.files.length === 0) return false;
          onFilesDropped(selection.files);
          return true;
        })
        .catch((error) => {
          void logger.error(
            "ui",
            "chat-dropped-files-read-failed",
            "Failed to read dropped files",
            error instanceof Error ? error : new Error(String(error))
          );
          toast.error(t.documents.chatSidebar.drop.readFilesFailed);
          return false;
        });
    },
    [clear, onDocumentsDropped, onFilesDropped, t.documents.chatSidebar.drop.readFilesFailed]
  );

  const handleCollapsedDragOver = useCallback(
    (e: DragEvent<HTMLElement>) => {
      handleDragOver(e);
      if (!dragKindOf(e.dataTransfer) || isInsideModal(e.target)) {
        cancelSpringLoad();
        return;
      }
      if (springLoadTimer.current !== null) return;
      springLoadTimer.current = setTimeout(() => {
        springLoadTimer.current = null;
        onExpand();
      }, SPRING_LOAD_DELAY_MS);
    },
    [cancelSpringLoad, handleDragOver, onExpand]
  );

  const handleCollapsedDragLeave = useCallback(
    (e: DragEvent<HTMLElement>) => {
      cancelSpringLoad();
      handleDragLeave(e);
    },
    [cancelSpringLoad, handleDragLeave]
  );

  const handleCollapsedDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      cancelSpringLoad();
      const accepted = handleDrop(e);
      if (typeof accepted === "boolean") {
        if (accepted) onExpand();
      } else {
        void accepted.then((didAccept) => {
          if (didAccept) onExpand();
        });
      }
    },
    [cancelSpringLoad, handleDrop, onExpand]
  );

  const handleExpandedDrop = useCallback(
    (e: DragEvent<HTMLElement>) => {
      void handleDrop(e);
    },
    [handleDrop]
  );

  const dropZoneProps: DropHandlers = {
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleExpandedDrop,
  };
  const collapsedDropProps: DropHandlers = {
    onDragOver: handleCollapsedDragOver,
    onDragLeave: handleCollapsedDragLeave,
    onDrop: handleCollapsedDrop,
  };

  return {
    /** What the drag over the chat would attach; null when nothing is over it. */
    dragKind: highlight.isActive ? dragKind : null,
    dropZoneProps,
    collapsedDropProps,
  };
}
